import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isAdmin as checkAdmin, getAdminClientData } from '../../utils/authUtils';

const AdminContext = createContext({
    isAdmin: false,
    clientsList: [],
    selectedClient: null,
    viewMode: 'admin',
    setSelectedClient: () => { },
    clearSelectedClient: () => { },
    setViewMode: () => { },
    toggleViewMode: () => { },
});

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
    const [isAdminUser] = useState(() => checkAdmin());
    const [clientsList] = useState(() => getAdminClientData());

    // Persist selected client across page refreshes in sessionStorage
    const [selectedClient, setSelectedClientState] = useState(() => {
        try {
            const saved = sessionStorage.getItem('adminSelectedClient');
            return saved ? JSON.parse(saved) : null;
        } catch { return null; }
    });

    // ── View Mode: 'admin' = admin-level tools, 'client' = client config ──
    const [viewMode, setViewModeState] = useState(() => {
        try {
            const saved = sessionStorage.getItem('adminViewMode');
            if (saved === 'admin' || saved === 'client') return saved;
            // Default: admin mode if no client was previously selected
            const hasClient = !!sessionStorage.getItem('adminSelectedClient');
            return hasClient ? 'client' : 'admin';
        } catch { return 'admin'; }
    });

    const setViewMode = useCallback((mode) => {
        setViewModeState(mode);
        sessionStorage.setItem('adminViewMode', mode);
        // Broadcast so AccessContext, homePage, etc. can react instantly
        window.dispatchEvent(new CustomEvent('adminViewModeChanged', { detail: mode }));
    }, []);

    const toggleViewMode = useCallback(() => {
        setViewMode(viewMode === 'admin' ? 'client' : 'admin');
    }, [viewMode, setViewMode]);

    const setSelectedClient = useCallback((client) => {
        setSelectedClientState(client);
        if (client) {
            sessionStorage.setItem('adminSelectedClient', JSON.stringify(client));
        } else {
            sessionStorage.removeItem('adminSelectedClient');
        }
        // Fire event so AccessContext re-fetches permissions for new client
        window.dispatchEvent(new CustomEvent('adminClientChanged', { detail: client }));
    }, []);

    const clearSelectedClient = useCallback(() => {
        setSelectedClient(null);
    }, [setSelectedClient]);

    // On logout (credentials cleared), also clear selected client + view mode
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === 'credentials' && !e.newValue) {
                setSelectedClientState(null);
                setViewModeState('admin');
                sessionStorage.removeItem('adminSelectedClient');
                sessionStorage.removeItem('adminViewMode');
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    return (
        <AdminContext.Provider value={{
            isAdmin: isAdminUser,
            clientsList,
            selectedClient,
            viewMode,
            setSelectedClient,
            clearSelectedClient,
            setViewMode,
            toggleViewMode,
        }}>
            {children}
        </AdminContext.Provider>
    );
};

export default AdminContext;
